/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Component imports
import { Card, Checkbox, Col, Form, InputNumber, Row, Typography } from 'antd';
import DocumentTitle from '../components/DocumentTitle';
import openConsoleAlertMessage from '../utils/misc/openConsoleAlertMessage';

// Constant declarations
const { Paragraph, Title } = Typography;
const COSTS = {
  BASE: 100,
  WEBCHAT: 0.03,
  FACEBOOK: 0.03,
  WHATSAPP: 0.07,
  LIVECHAT: 20,
  NLP: 0.02,
  LANGUAGE: 0.01
};

const PricingScreen = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [, updateState] = useState(); // Hook to force a component re-render

  const calculateTotalSessions = () => {
    let total = 0;

    if (form.getFieldValue('checkWebchat') && form.getFieldValue('webchat') !== undefined) {
      total += parseInt(form.getFieldValue('webchat'));
    }
    if (form.getFieldValue('checkFacebook') && form.getFieldValue('facebook') !== undefined) {
      total += parseInt(form.getFieldValue('facebook'));
    }
    if (form.getFieldValue('checkWhatsapp') && form.getFieldValue('whatsapp') !== undefined) {
      total += parseInt(form.getFieldValue('whatsapp'));
    }
    if (isNaN(total)) {
      return 0;
    }

    return total;
  };

  const calculateChannelsCost = () => {
    let total = 0;

    if (form.getFieldValue('checkWebchat') && form.getFieldValue('webchat') !== undefined) {
      total += COSTS.WEBCHAT * parseInt(form.getFieldValue('webchat'));
    }
    if (form.getFieldValue('checkFacebook') && form.getFieldValue('facebook') !== undefined) {
      total += COSTS.FACEBOOK * parseInt(form.getFieldValue('facebook'));
    }
    if (form.getFieldValue('checkWhatsapp') && form.getFieldValue('whatsapp') !== undefined) {
      total += COSTS.WHATSAPP * parseInt(form.getFieldValue('whatsapp'));
    }
    if (isNaN(total)) {
      return 0.00;
    }

    return total.toFixed(2);
  };

  const calculateLiveChatCost = () => {
    let total = 0;

    if (form.getFieldValue('checkLivechat') && form.getFieldValue('livechat') !== undefined) {
      total = COSTS.LIVECHAT * parseInt(form.getFieldValue('livechat'));
    }
    if (isNaN(total)) {
      return 0.00;
    }

    return total.toFixed(2);
  };

  const calculatePowerupsCost = () => {
    let total = 0;
    let sessions = calculateTotalSessions();

    if (sessions === 0 || sessions === undefined) {
      return 0.00;
    }
    if (form.getFieldValue('checkNlp')) {
      total += sessions * COSTS.NLP;
    }
    if (form.getFieldValue('checkLanguageDetection')) {
      total += sessions * COSTS.LANGUAGE;
    }
    if (isNaN(total)) {
      return 0.00;
    }

    return total.toFixed(2);
  };

  const calculateTotalCost = () => {
    let total = COSTS.BASE + parseFloat(calculateChannelsCost()) + parseFloat(calculateLiveChatCost()) + parseFloat(calculatePowerupsCost());
    return total.toFixed(2);
  };

  const onFinishFailed = () => {
    openConsoleAlertMessage({ type: 'error', message: t('Could not save values. Ensure you entered everything correctly and try again.') });
  };

  return (
    <Fragment>
      <DocumentTitle title={t('Pricing calculator')} />

      <Title level={3}>{t('Pricing calculator')}</Title>

      <Form
        name="pricingForm"
        layout="inline"
        onFinishFailed={onFinishFailed}
        form={form}
        onFieldsChange={(_, allFields) => {
          updateState(allFields);
        }}
      >
        <Row gutter={[16, 20]} className="w-100">
          <Col xs={24} lg={12}>
            <Card title={t('Channels and volumes')} bordered={false} className="h-100">
              <Row gutter={16} className="align-items-baseline mb-3">
                <Col xs={24} sm={5} lg={24} xl={5}>
                  {t('Webchat')}:
                </Col>
                <Col className="d-flex">
                  <Form.Item
                    name="checkWebchat"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                  <Form.Item
                    name="webchat"
                    required={[{ required: false }]}
                  >
                    <InputNumber min={0}/>
                  </Form.Item>
                  {t('sessions per month')}
                </Col>
              </Row>

              <Row gutter={16} className="align-items-baseline mb-3">
                <Col xs={24} sm={5} lg={24} xl={5}>
                  {t('Facebook')}:
                </Col>
                <Col className="d-flex">
                  <Form.Item
                    name="checkFacebook"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                  <Form.Item
                    name="facebook"
                    required={[{ required: false }]}
                  >
                    <InputNumber min={0}/>
                  </Form.Item>
                  {t('sessions per month')}
                </Col>
              </Row>

              <Row gutter={16} className="align-items-baseline mb-3">
                <Col xs={24} sm={5} lg={24} xl={5}>
                  {t('Whatsapp')}:
                </Col>
                <Col className="d-flex">
                  <Form.Item
                    name="checkWhatsapp"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                  <Form.Item
                    name="whatsapp"
                    required={[{ required: false }]}
                  >
                    <InputNumber min={0}/>
                  </Form.Item>
                  {t('sessions per month')}
                </Col>
              </Row>

              <Row gutter={16} className="align-items-baseline mb-3">
                <Col xs={24} sm={5} lg={24} xl={5}>
                  {t('LiveChat')}:
                </Col>
                <Col className="d-flex">
                  <Form.Item
                    name="checkLivechat"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                  <Form.Item
                    name="livechat"
                    required={[{ required: false }]}
                  >
                    <InputNumber min={0}/>
                  </Form.Item>
                  {t('seats')}
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title={t('Powerups')} bordered={false} className="h-100">
              <Row>
                <Col xs={12} sm={10} md={10} lg={16} xl={10}>
                  {t('NLP powered Automated Answers')}:
                </Col>
                <Col>
                  <Form.Item
                    name="checkNlp"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col xs={12} sm={10} md={10} lg={16} xl={10}>
                  {t('Language detection')}:
                </Col>
                <Col>
                  <Form.Item
                    name="checkLanguageDetection"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title={t('Cost analysis')} bordered={false} className="h-100">
              <Paragraph>
                {t('Base agent cost')}:
                <span className="px-3">{COSTS.BASE.toFixed(2)}$</span>
                / {t('month')}
              </Paragraph>
              <Paragraph>
                {t('Channels cost')}:
                <span className="px-3">{calculateChannelsCost()}$</span>
                / {t('month')}
              </Paragraph>
              <Paragraph>
                {t('LiveChat cost')}:
                <span className="px-3">{calculateLiveChatCost()}$</span>
                / {t('month')}
              </Paragraph>
              <Paragraph>
                {t('Powerups cost')}:
                <span className="px-3">{calculatePowerupsCost()}$</span>
                / {t('month')}
              </Paragraph>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title={t('Cost analysis')} bordered={false} className="h-100 ant-card-success">
              <Paragraph>
                {t('Total sessions')}:
                <span className="px-5">{calculateTotalSessions()}</span>
              </Paragraph>
              <Paragraph className="fs-xxl">
                {t('Total cost')}:
                <span className="px-5">{calculateTotalCost()}$</span>
                / {t('month')}
              </Paragraph>
            </Card>
          </Col>
        </Row>
      </Form>
    </Fragment>
  );
};

export default PricingScreen;
